import type { TreatmentType } from "@src/packages/homepage/utils/costEstimator";

export type EstimateLineId =
  | "consult"
  | "female-workup"
  | "male-workup"
  | "uterine-cavity"
  | "egg-freezing-cycle"
  | "embryo-freezing-cycle"
  | "ivf-cycle"
  | "fet-cycle"
  | "iui-cycle"
  | "oi-cycle"
  | "sperm-freezing-cycle"
  | "anesthesia"
  | "medications-stimulation"
  | "medications-fet"
  | "medications-iui"
  | "medications-oi"
  | "icsi"
  | "pgta"
  | "pgtm-sr"
  | "pgtp"
  | "assisted-hatching"
  | "sperm-wash"
  | "donor-sperm"
  | "donor-eggs"
  | "donor-embryo"
  | "known-donor-coordination"
  | "storage"
  | "specimen-transfer"
  | "additional-fet"
  | "transfer-meds"
  | "extra-cycle-buffer";

export type CostSourceQuality =
  | "clinic-quote"
  | "clinic-published"
  | "regional-study"
  | "public-average"
  | "modeled";

export type CostPriceType =
  "cash-pay" | "list-price" | "insurance-allowed" | "patient-paid" | "unknown";

export type CostMethodology =
  | "observed-clinic-price"
  | "nearby-clinic-average"
  | "regional-observed-average"
  | "national-public-reference"
  | "clinical-planning-model";

export type CostAvailabilityStatus =
  | "available"
  | "restricted"
  | "not-available"
  | "funded"
  | "partially-funded"
  | "unknown";

export type CostRestrictionReason =
  | "not-legal"
  | "not-offered"
  | "medical-criteria"
  | "age-limit"
  | "marital-status"
  | "residency"
  | "public-system-capacity"
  | "clinic-policy"
  | "unknown";

export type CostFundingModel =
  | "self-pay"
  | "insurance"
  | "public"
  | "mixed"
  | "employer-benefit"
  | "unknown";

export type CostLocationScope =
  "clinic" | "city" | "state" | "country" | "global";

export type CostCatalogSource = {
  id: string;
  name: string;
  url?: string;
  publishedAt?: string;
  retrievedAt: string;
  quality: CostSourceQuality;
};

export type CostCatalogLocation = {
  scope: CostLocationScope;
  countryCode: string;
  stateCode?: string;
  city?: string;
  clinicId?: string;
  latitude?: number;
  longitude?: number;
};

export type CostAvailabilityPolicy = {
  status: CostAvailabilityStatus;
  fundingModel: CostFundingModel;
  restrictionReasons?: CostRestrictionReason[];
  fundedCyclesLimit?: number;
  fundedTransfersLimit?: number;
  ageLimitYears?: number;
  residencyRequired?: boolean;
  expectedWaitMonths?: {
    min: number;
    max: number;
  };
  privatePayAllowed?: boolean;
  crossBorderCommonDestinations?: string[];
  notes?: string;
  source?: CostCatalogSource;
};

export type CostCurrency = "USD" | "EUR";

export type CostCatalogEntry = {
  id: string;
  lineId: EstimateLineId;
  amount: number;
  range?: {
    low: number;
    high: number;
  };
  currency: CostCurrency;
  unit: "once" | "cycle" | "embryo" | "storage-year" | "transfer";
  location: CostCatalogLocation;
  priceType: CostPriceType;
  methodology: CostMethodology;
  availability: CostAvailabilityPolicy;
  sampleSize?: number;
  appliesTo?: TreatmentType[];
  source: CostCatalogSource;
  effectiveFrom: string;
  lastReviewedAt: string;
  confidence: "high" | "medium" | "low";
};

export type FertilityCostCatalog = {
  version: string;
  effectiveFrom: string;
  generatedAt: string;
  defaultCurrency: CostCurrency;
  notes: string[];
  sources: CostCatalogSource[];
  entries: CostCatalogEntry[];
};

const CARE_CREDIT_SOURCE: CostCatalogSource = {
  id: "carecredit-ivf-pricing-2025",
  name: "CareCredit IVF pricing overview",
  url: "https://www.carecredit.com/well-u/health-wellness/ivf-pricing/",
  publishedAt: "2025",
  retrievedAt: "2026-05-24",
  quality: "public-average",
};

const STATE_DIAGNOSTIC_SOURCE: CostCatalogSource = {
  id: "state-infertility-workup-costs-2024",
  name: "State-level infertility workup costs study",
  url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11705584/",
  publishedAt: "2024",
  retrievedAt: "2026-05-24",
  quality: "regional-study",
};

const MODELED_SOURCE: CostCatalogSource = {
  id: "clinical-planning-model-2026-05",
  name: "Clinical planning model",
  retrievedAt: "2026-05-24",
  quality: "modeled",
};

const POLICY_MODEL_SOURCE: CostCatalogSource = {
  id: "country-policy-model-2026-05",
  name: "Country access policy model",
  retrievedAt: "2026-05-24",
  quality: "modeled",
};

const US_COUNTRY_LOCATION: CostCatalogLocation = {
  scope: "country",
  countryCode: "US",
};

function usEntry(
  lineId: EstimateLineId,
  amount: number,
  unit: CostCatalogEntry["unit"],
  confidence: CostCatalogEntry["confidence"] = "medium",
  source: CostCatalogSource = CARE_CREDIT_SOURCE,
  range?: CostCatalogEntry["range"],
): CostCatalogEntry {
  return {
    id: `us-${lineId}`,
    lineId,
    amount,
    range,
    currency: "USD",
    unit,
    location: US_COUNTRY_LOCATION,
    priceType: "cash-pay",
    methodology:
      source.quality === "modeled"
        ? "clinical-planning-model"
        : source.quality === "regional-study"
          ? "regional-observed-average"
          : "national-public-reference",
    availability: {
      status: "available",
      fundingModel: "self-pay",
      source: source.quality === "modeled" ? POLICY_MODEL_SOURCE : source,
    },
    source,
    effectiveFrom: "2026-05-24",
    lastReviewedAt: "2026-05-24",
    confidence,
  };
}

const LINE_UNITS: Partial<Record<EstimateLineId, CostCatalogEntry["unit"]>> = {
  "egg-freezing-cycle": "cycle",
  "embryo-freezing-cycle": "cycle",
  "ivf-cycle": "cycle",
  "fet-cycle": "transfer",
  "iui-cycle": "cycle",
  "oi-cycle": "cycle",
  "sperm-freezing-cycle": "cycle",
  anesthesia: "cycle",
  "medications-stimulation": "cycle",
  "medications-fet": "transfer",
  "medications-iui": "cycle",
  "medications-oi": "cycle",
  icsi: "cycle",
  pgta: "embryo",
  pgtp: "embryo",
  "pgtm-sr": "once",
  "assisted-hatching": "cycle",
  "sperm-wash": "cycle",
  storage: "storage-year",
  "additional-fet": "transfer",
  "transfer-meds": "transfer",
};

function countryPolicyEntry({
  countryCode,
  lineId,
  amount = 0,
  currency = "USD",
  availability,
}: {
  countryCode: string;
  lineId: EstimateLineId;
  amount?: number;
  currency?: CostCurrency;
  availability: CostAvailabilityPolicy;
}): CostCatalogEntry {
  return {
    id: `${countryCode.toLowerCase()}-${lineId}-policy`,
    lineId,
    amount,
    currency,
    unit: LINE_UNITS[lineId] ?? "once",
    location: {
      scope: "country",
      countryCode,
    },
    priceType:
      availability.status === "funded" ||
      availability.status === "partially-funded"
        ? "patient-paid"
        : "unknown",
    methodology: "clinical-planning-model",
    availability,
    source: availability.source ?? POLICY_MODEL_SOURCE,
    effectiveFrom: "2026-05-24",
    lastReviewedAt: "2026-05-24",
    confidence: "low",
  };
}

export const FERTILITY_COST_CATALOG: FertilityCostCatalog = {
  version: "2026.05.24-schema-seed",
  effectiveFrom: "2026-05-24",
  generatedAt: "2026-05-24",
  defaultCurrency: "USD",
  notes: [
    "This is a schema seed, not comprehensive coverage.",
    "Clinic, city, state, and global rows should be ingested as observed price facts rather than added ad hoc in UI code.",
    "Country rows are fallbacks only and should lose priority as clinic and regional coverage improves.",
  ],
  sources: [
    CARE_CREDIT_SOURCE,
    STATE_DIAGNOSTIC_SOURCE,
    MODELED_SOURCE,
    POLICY_MODEL_SOURCE,
  ],
  entries: [
    usEntry("consult", 220, "once", "low", MODELED_SOURCE, {
      low: 0,
      high: 500,
    }),
    usEntry("female-workup", 650, "once", "medium", STATE_DIAGNOSTIC_SOURCE),
    usEntry("male-workup", 250, "once", "medium", STATE_DIAGNOSTIC_SOURCE),
    usEntry("uterine-cavity", 950, "once", "medium", STATE_DIAGNOSTIC_SOURCE),
    usEntry("egg-freezing-cycle", 8800, "cycle"),
    usEntry("embryo-freezing-cycle", 10500, "cycle"),
    usEntry("ivf-cycle", 11200, "cycle"),
    usEntry("fet-cycle", 4500, "transfer"),
    usEntry("iui-cycle", 2000, "cycle"),
    usEntry("oi-cycle", 1600, "cycle", "low", MODELED_SOURCE),
    usEntry("sperm-freezing-cycle", 1000, "cycle"),
    usEntry("anesthesia", 750, "cycle"),
    usEntry("medications-stimulation", 3991, "cycle"),
    usEntry("medications-fet", 750, "transfer"),
    usEntry("medications-iui", 775, "cycle"),
    usEntry("medications-oi", 152, "cycle"),
    usEntry("icsi", 1500, "cycle"),
    usEntry("pgta", 650, "embryo", "low", MODELED_SOURCE),
    usEntry("pgtm-sr", 3200, "once", "low", MODELED_SOURCE),
    usEntry("pgtp", 800, "embryo", "low", MODELED_SOURCE),
    usEntry("assisted-hatching", 650, "cycle"),
    usEntry("sperm-wash", 350, "cycle"),
    usEntry("donor-sperm", 1200, "cycle", "low", MODELED_SOURCE),
    usEntry("donor-eggs", 21000, "once", "low", MODELED_SOURCE),
    usEntry("donor-embryo", 3000, "once", "low", MODELED_SOURCE),
    usEntry("known-donor-coordination", 2500, "once", "low", MODELED_SOURCE),
    usEntry("storage", 950, "storage-year", "low", MODELED_SOURCE),
    usEntry("specimen-transfer", 650, "once", "low", MODELED_SOURCE),
    usEntry("additional-fet", 4800, "transfer", "low", MODELED_SOURCE),
    usEntry("transfer-meds", 500, "transfer", "low", MODELED_SOURCE),
    usEntry("extra-cycle-buffer", 11200, "once", "low", MODELED_SOURCE),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "ivf-cycle",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        fundedCyclesLimit: 4,
        ageLimitYears: 43,
        restrictionReasons: [
          "medical-criteria",
          "age-limit",
          "residency",
          "public-system-capacity",
        ],
        residencyRequired: true,
        expectedWaitMonths: { min: 6, max: 24 },
        privatePayAllowed: true,
        crossBorderCommonDestinations: ["ES", "BE"],
        notes:
          "Fully covered by Assurance Maladie: up to 4 IVF attempts for women up to age 43. An attempt counts only when a retrieval produces at least one transfer; cancelled retrievals don't count. Counter resets only on live birth.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "consult",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        privatePayAllowed: true,
        notes:
          "AMP consultations are reimbursed by Assurance Maladie as part of the ALD-PMA 100% coverage. Secteur-2 clinics may add dépassements d'honoraires covered by mutuelle.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "female-workup",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Hormone panels, AMH and baseline ultrasound are covered under the AMP pathway via Assurance Maladie.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "male-workup",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Semen analysis and partner infectious labs are covered under the AMP pathway via Assurance Maladie.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "uterine-cavity",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "HSG and cavity assessment imaging are covered under Assurance Maladie when ordered as part of the AMP workup.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "fet-cycle",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Frozen embryo transfers are covered under the same ALD-PMA framework and count toward the 4 IVF attempts.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "anesthesia",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Retrieval anesthesia is bundled into the hospital stay and covered by Assurance Maladie.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "medications-stimulation",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Stimulation medications prescribed as part of a covered PMA cycle are reimbursed at 100% via Assurance Maladie.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "medications-fet",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Transfer preparation medications are reimbursed at 100% when prescribed for a covered FET cycle.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "medications-iui",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "IUI medications are reimbursed at 100% when prescribed as part of a covered IUI cycle.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "icsi",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "ICSI is covered by Assurance Maladie when medically indicated (typically male-factor infertility).",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "assisted-hatching",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Assisted hatching is covered when it's part of a clinic's standard laboratory protocol for the covered PMA cycle.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "sperm-wash",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes: "Sperm preparation is covered as part of a covered PMA cycle.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "specimen-transfer",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Transfer of frozen gametes or embryos between CECOS centres is coordinated through the public system.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "additional-fet",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Additional frozen transfers are covered within the 4 IVF attempts.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "transfer-meds",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Transfer medications are reimbursed at 100% when prescribed for a covered transfer cycle.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "storage",
      amount: 41,
      currency: "EUR",
      availability: {
        status: "available",
        fundingModel: "self-pay",
        privatePayAllowed: true,
        notes:
          "Gamete and embryo storage is handled by CECOS. Annual tariff is ~€40.50/year (CECOS standard, 2025).",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "pgtp",
      availability: {
        status: "not-available",
        fundingModel: "unknown",
        restrictionReasons: ["not-legal"],
        privatePayAllowed: false,
        crossBorderCommonDestinations: ["ES"],
        notes:
          "Placeholder policy row: polygenic embryo screening availability must be verified before surfacing this option.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "pgta",
      availability: {
        status: "not-available",
        fundingModel: "unknown",
        restrictionReasons: ["not-legal"],
        privatePayAllowed: false,
        crossBorderCommonDestinations: ["BE", "ES"],
        notes:
          "PGT-A (preimplantation genetic testing for aneuploidy) is not legally offered in France under current bioethics law. Patients commonly travel to Belgium or Spain for this. Only PGT-M and PGT-SR are permitted via CPDPN authorization. Source: Arrêté du 18 juin 2024.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "pgtm-sr",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        restrictionReasons: ["medical-criteria"],
        privatePayAllowed: false,
        notes:
          "PGT-M (monogenic conditions) and PGT-SR (structural rearrangements) are legal in France and 100% reimbursed by Assurance Maladie when authorized by a CPDPN. Requires a known inherited condition in the family. 5 centres: Paris, Strasbourg, Montpellier, Nantes, Grenoble.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "iui-cycle",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        fundedCyclesLimit: 6,
        ageLimitYears: 43,
        restrictionReasons: ["medical-criteria", "age-limit"],
        privatePayAllowed: true,
        notes:
          "Up to 6 IUI attempts fully covered by Assurance Maladie for women up to age 43. Counter resets only on live birth.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "egg-freezing-cycle",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        ageLimitYears: 37,
        restrictionReasons: ["age-limit", "residency"],
        residencyRequired: true,
        privatePayAllowed: false,
        notes:
          "Elective self-preservation of oocytes is publicly funded for women aged 29–37 since the 2021 bioethics law. Retrieval and freezing are 100% covered; patient pays only annual storage.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "embryo-freezing-cycle",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Embryo freezing performed as part of a covered IVF cycle is reimbursed by Assurance Maladie.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "sperm-freezing-cycle",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Sperm freezing is covered by Assurance Maladie when performed through CECOS, including fertility-preservation indications (oncology, surgery).",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "oi-cycle",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Ovulation induction monitoring is covered under the AMP pathway via Assurance Maladie.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "medications-oi",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        notes:
          "Ovulation induction medications are reimbursed at 100% when prescribed for a covered OI cycle.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "donor-sperm",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        restrictionReasons: ["public-system-capacity", "residency"],
        residencyRequired: true,
        expectedWaitMonths: { min: 12, max: 18 },
        privatePayAllowed: false,
        notes:
          "Donor sperm is distributed exclusively through CECOS under the anonymous public donation framework. Typical wait 12–18 months. No private sperm bank is permitted in France.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "donor-eggs",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        restrictionReasons: ["public-system-capacity", "residency"],
        residencyRequired: true,
        expectedWaitMonths: { min: 20, max: 30 },
        privatePayAllowed: false,
        crossBorderCommonDestinations: ["ES"],
        notes:
          "Donor eggs are distributed exclusively through CECOS under the anonymous public donation framework. Typical wait ~22 months (longer in Île-de-France). Many patients travel to Spain to avoid the wait.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "donor-embryo",
      currency: "EUR",
      availability: {
        status: "funded",
        fundingModel: "public",
        restrictionReasons: ["public-system-capacity", "residency"],
        residencyRequired: true,
        privatePayAllowed: false,
        notes:
          "Donor embryo coordination is handled through CECOS under the anonymous donation framework.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "FR",
      lineId: "known-donor-coordination",
      availability: {
        status: "not-available",
        fundingModel: "unknown",
        restrictionReasons: ["not-legal"],
        privatePayAllowed: false,
        notes:
          "Known-donor coordination is not permitted in France. Donor gametes must be collected and distributed by CECOS under the anonymous public donation framework. A patient cannot bring their own identified donor.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "DE",
      lineId: "pgtp",
      availability: {
        status: "not-available",
        fundingModel: "unknown",
        restrictionReasons: ["not-legal"],
        privatePayAllowed: false,
        crossBorderCommonDestinations: ["ES", "CZ"],
        notes:
          "Placeholder policy row: embryo genetic testing rules vary by indication and require country-specific review.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
    countryPolicyEntry({
      countryCode: "ES",
      lineId: "pgtp",
      availability: {
        status: "restricted",
        fundingModel: "self-pay",
        restrictionReasons: ["clinic-policy"],
        privatePayAllowed: true,
        notes:
          "Placeholder policy row: private-pay cross-border care may be faster, but exact test availability depends on clinic and lab policy.",
        source: POLICY_MODEL_SOURCE,
      },
    }),
  ],
};
